import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { useModule } from '../../context/ModuleContext';
import { Permissions } from '../../constants/Permissions';
import { dataImportService, ImportEntity, ImportResult } from '../../services/dataImportService';
import { parseApiError } from '../../utils/errorUtils';
import './DataImportPage.css';
import {
  ChefHat, Download, FlaskConical, Package, Receipt, ShoppingBag, Truck, Upload, Users,
} from 'lucide-react';

interface EntityConfig {
  key: ImportEntity;
  icon: React.ReactNode;
  permission: string;
  restaurantOnly?: boolean;
}

const ENTITIES: EntityConfig[] = [
  { key: 'products', icon: <Package size={20} strokeWidth={1.75} />, permission: Permissions.Products.Create },
  { key: 'purchase-items', icon: <ShoppingBag size={20} strokeWidth={1.75} />, permission: Permissions.PurchaseOrders.Create },
  { key: 'sales-items', icon: <Receipt size={20} strokeWidth={1.75} />, permission: Permissions.SalesInvoices.Create },
  { key: 'suppliers', icon: <Truck size={20} strokeWidth={1.75} />, permission: Permissions.Suppliers.Create },
  { key: 'customers', icon: <Users size={20} strokeWidth={1.75} />, permission: Permissions.Customers.Create },
  { key: 'menu-items', icon: <ChefHat size={20} strokeWidth={1.75} />, permission: Permissions.Menu.Create, restaurantOnly: true },
  { key: 'recipes', icon: <FlaskConical size={20} strokeWidth={1.75} />, permission: Permissions.Menu.Create, restaurantOnly: true },
];

const DataImportPage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const { isModule } = useModule();

  const [busyEntity, setBusyEntity] = useState<ImportEntity | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingEntity = useRef<ImportEntity | null>(null);

  const visibleEntities = ENTITIES.filter(
    (e) => hasPermission(e.permission) && (!e.restaurantOnly || isModule('restaurant'))
  );

  const entityLabel = (key: ImportEntity) => t(`dataImport.entities.${key}.title`);

  const handleDownloadTemplate = async (entity: ImportEntity) => {
    setError(null);
    try {
      await dataImportService.downloadTemplate(entity);
    } catch (err: unknown) {
      setError(parseApiError(err, t('dataImport.failedToDownloadTemplate')).message);
    }
  };

  const handleChooseFile = (entity: ImportEntity) => {
    pendingEntity.current = entity;
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const entity = pendingEntity.current;
    e.target.value = '';   // allow re-selecting the same file
    if (!file || !entity) return;

    setError(null);
    setResult(null);
    setBusyEntity(entity);
    try {
      const response = await dataImportService.importFile(entity, file);
      setResult(response.data.data);
    } catch (err: unknown) {
      setError(parseApiError(err, t('dataImport.failedToImport')).message);
    } finally {
      setBusyEntity(null);
    }
  };

  return (
    <Layout>
      <div className="ims-page data-import-page">
        <div className="ims-list-header">
          <h1 className="ims-list-title">{t('dataImport.title')}</h1>
        </div>

        <p className="di-intro">{t('dataImport.intro')}</p>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            <strong>{t('common.error')}:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        {/* Hidden shared file input — the clicked card sets pendingEntity */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx"
          style={{ display: 'none' }}
          onChange={handleFileSelected}
        />

        <div className="di-grid">
          {visibleEntities.map((entity) => (
            <div className="di-entity-card" key={entity.key}>
              <div className="di-entity-head">
                <div className="di-entity-icon">{entity.icon}</div>
                <h2 className="di-entity-title">{entityLabel(entity.key)}</h2>
              </div>
              <p className="di-entity-desc">{t(`dataImport.entities.${entity.key}.description`)}</p>
              <div className="di-entity-actions">
                <button
                  type="button"
                  className="ims-btn ims-btn-secondary"
                  onClick={() => handleDownloadTemplate(entity.key)}
                  disabled={busyEntity !== null}
                >
                  <Download size={14} strokeWidth={2} />
                  {t('dataImport.downloadTemplate')}
                </button>
                <button
                  type="button"
                  className="ims-btn ims-btn-primary"
                  onClick={() => handleChooseFile(entity.key)}
                  disabled={busyEntity !== null}
                >
                  {busyEntity === entity.key
                    ? <><span className="ims-btn-spinner" />{t('dataImport.uploading')}</>
                    : <><Upload size={14} strokeWidth={2} />{t('dataImport.uploadFile')}</>}
                </button>
              </div>
            </div>
          ))}
        </div>

        {result && (
          <div className="ims-card">
            <div className="ims-card-header">
              <span className="ims-card-title">
                {t('dataImport.resultTitle', { entity: entityLabel(result.entity as ImportEntity) })}
              </span>
            </div>
            <div className="ims-card-body">
              <div className="di-summary">
                <span className="di-summary-item">
                  {t('dataImport.totalRows')}<strong>{result.totalRows}</strong>
                </span>
                <span className="di-summary-item">
                  {t('dataImport.importedRows')}<strong>{result.importedCount}</strong>
                </span>
                <span className="di-summary-item">
                  {t('dataImport.failedRows')}<strong>{result.failedCount}</strong>
                </span>
              </div>

              {result.failedCount === 0 ? (
                <div className="ims-alert ims-alert-success">{t('dataImport.allRowsImported')}</div>
              ) : (
                <>
                  <div className="ims-alert ims-alert-warning">{t('dataImport.someRowsFailed')}</div>
                  <table className="di-results-table">
                    <thead>
                      <tr>
                        <th className="di-row-number">{t('dataImport.rowColumn')}</th>
                        <th>{t('dataImport.errorColumn')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.errors.map((rowError, index) => (
                        <tr key={index}>
                          <td className="di-row-number">{rowError.row}</td>
                          <td>{rowError.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default DataImportPage;
